/**
 * # Eurail Search Results
 */
import { ElementsSDK } from '@yiluhub/ui-sdk-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { getCurrentLanguage } from 'utils/i18n';

import { SearchResultCard, SearchResultNotFound } from 'components';

import { getLocationLabel } from 'modules/eurail/utils/locationData';
import { splitByPassType } from 'modules/eurail/utils/sortResults';
import { PassType } from 'modules/eurail/utils/types';

import EurailSrpResultCard from '../EurailSrpResultCard';
import { useEurailSearchResultsLoader } from './hooks';
import { EurailSearchResultsProps } from './types';

export const EurailSearchResults: React.FC<EurailSearchResultsProps> = ({
  searchParams,
  onSearchResults,
  onSearchResultClick,
  onError,
}) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();

  const { isLoading, searchResults } = useEurailSearchResultsLoader({
    searchParams,
    onSearchResults,
    onError,
  });

  const { continuous, flexi } = splitByPassType(searchResults);

  // 💡 Small helper to render a pass-type section
  const renderSection = (titleKey: string, results: typeof searchResults) => {
    if (!results.length) return null;

    return (
      <div className="eurail-Search-Results__Results-Section">
        <div className="eurail-Search-Results__Section-Header">
          <ElementsSDK.Typography variant="h3">{t(titleKey)}</ElementsSDK.Typography>
        </div>

        <div className="eurail-Search-Results__Results-Grid">
          {results.map((searchResult) => (
            <EurailSrpResultCard
              key={`eurail-srp-result-card-${uuidv4()}`}
              data={searchResult}
              onClick={onSearchResultClick}
            />
          ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <section>
        <div className="eurail-Search-Results__Section">
          <SearchResultCard asPlaceholder />
        </div>
      </section>
    );
  }

  if (!searchResults.length) {
    return (
      <SearchResultNotFound
        title={t('No results found')}
        message={t('eurail.global.no.results.found')}
      />
    );
  }

  return (
    <section>
      <div className="eurail-Search-Results__Section">
        <ElementsSDK.Typography
          testid="eurailSearchResults__title"
          variant="h2"
          className="eurail-Search-Results__Title"
        >
          {searchParams.pass === PassType.ONE_COUNTRY
            ? t('eurail.srp.country.title', {
                country: getLocationLabel(searchParams.to, currentLanguage),
              })
            : t('eurail.srp.global.title')}
        </ElementsSDK.Typography>

        <ElementsSDK.Message
          content={t('eurail.searchresults.info.banner.text')}
          className="eurail-Search-Results__Info-Message"
        />

        <div className="eurail-Search-Results__Results-Section-Container">
          {renderSection('eurail.searchresults.flexi.text', flexi)}
          {renderSection('eurail.searchresults.continuous.text', continuous)}
        </div>
      </div>
    </section>
  );
};
